'use client';

import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/app/_components/core/button';
import { useLabel } from '@/app/_contexts/labels-context';
import { cn, emptyToUndefined } from '@/app/_lib/utils';

interface ExpandToggleProps {
  /**
   * Pre-rendered React elements from the Server Component layer.
   * These are passed as children (not rendered lazily) to allow Server Components
   * in the nested content. Do NOT wrap in Suspense or lazy-render inside this component.
   */
  children: ReactNode;
  buttonLabel?: string;
  trackingPrefix?: string;
}

export function ExpandToggle({ children, buttonLabel, trackingPrefix }: ExpandToggleProps) {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const regionId = useId();

  const defaultLabel = useLabel('global.loadMore', 'Mehr anzeigen');
  const label = emptyToUndefined(buttonLabel) ?? defaultLabel;

  useEffect(() => {
    if (!expanded || !containerRef.current) return;

    const el = containerRef.current;
    const onTransitionEnd = () => {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };
    el.addEventListener('transitionend', onTransitionEnd, { once: true });

    return () => {
      el.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [expanded]);

  return (
    <>
      <div
        ref={containerRef}
        id={regionId}
        aria-hidden={!expanded}
        inert={!expanded}
        className={cn(
          'grid transition-[grid-template-rows] duration-300 ease-in-out',
          expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
        )}
      >
        <div className="overflow-hidden">
          {children}
        </div>
      </div>

      {!expanded && (
        <div className="mt-8 flex justify-center">
          <Button
            variant="secondary"
            onClick={() => { setExpanded(true); }}
            aria-expanded={expanded}
            aria-controls={regionId}
            data-track-id={trackingPrefix ? `${trackingPrefix}-toggle` : undefined}
          >
            {label}
          </Button>
        </div>
      )}
    </>
  );
}
